import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { API, money } from "@/lib/site";

const inputCls = "w-full border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-regal-amber transition-colors";

export default function AdminFlex() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/flex-config`).then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load FLEX config"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setOpt = (list, i, k, v) =>
    setCfg((c) => ({ ...c, [list]: c[list].map((o, j) => (j === i ? { ...o, [k]: k === "price" ? v : v } : o)) }));

  const save = async () => {
    setBusy(true);
    try {
      await adminApi().put("/admin/flex-config", {
        hardware_price: parseFloat(cfg.hardware_price) || 0,
        linking_charge: parseFloat(cfg.linking_charge) || 0,
        master_options: cfg.master_options.map((o) => ({ name: o.name, price: parseFloat(o.price) || 0 })),
        slave_options: cfg.slave_options.map((o) => ({ name: o.name, price: parseFloat(o.price) || 0 })),
      });
      toast.success("FLEX options saved");
    } catch (e) {
      if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!cfg) return <main className="min-h-screen bg-surface pt-28"><div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div></main>;

  const Section = ({ title, list }) => (
    <section className="border border-line bg-white p-6 sm:p-8" data-testid={`admin-flex-${list}`}>
      <p className="overline-label mb-6">{title}</p>
      <div className="space-y-3">
        {cfg[list].map((o, i) => (
          <div key={i} className="flex items-center gap-3" data-testid={`admin-flex-option-${list}-${i}`}>
            <input value={o.name} onChange={(e) => setOpt(list, i, "name", e.target.value)} className={inputCls} data-testid={`admin-flex-name-${list}-${i}`} />
            <input type="number" step="0.01" min="0" value={o.price} onChange={(e) => setOpt(list, i, "price", e.target.value)} className={`${inputCls} w-32 shrink-0`} data-testid={`admin-flex-price-${list}-${i}`} />
            <button type="button" onClick={() => setCfg((c) => ({ ...c, [list]: c[list].filter((_, j) => j !== i) }))} aria-label="Remove option" data-testid={`admin-flex-remove-${list}-${i}`} className="grid h-10 w-10 shrink-0 place-items-center border border-line text-neutral-400 hover:border-regal-red hover:text-regal-red transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setCfg((c) => ({ ...c, [list]: [...c[list], { name: "", price: 0 }] }))} data-testid={`admin-flex-add-${list}`} className="inline-flex items-center gap-2 border border-dashed border-linebright px-5 py-2.5 text-sm text-neutral-500 hover:border-regal-amber hover:text-regal-amber transition-colors">
          <Plus size={14} /> Add option
        </button>
      </div>
    </section>
  );

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-flex-page">
      <div className="container-regal max-w-4xl">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-regal-amber" data-testid="admin-flex-back"><ArrowLeft size={15} /> All products</Link>
        <h1 className="mb-2 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Build Your FLEX — Options</h1>
        <p className="mb-8 text-sm text-neutral-400">Hardware price, linking charge and software packages used by the FLEX configurator. All prices in {cfg.currency || "GBP"}.</p>

        <div className="space-y-8">
          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Hardware & Linking</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">FLEX hardware price (EUR)</span>
                <input type="number" step="0.01" min="0" value={cfg.hardware_price} onChange={(e) => setCfg({ ...cfg, hardware_price: e.target.value })} className={inputCls} data-testid="admin-flex-hardware-input" />
              </label>
              <label className="block">
                <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Slave→other Master linking charge (EUR)</span>
                <input type="number" step="0.01" min="0" value={cfg.linking_charge} onChange={(e) => setCfg({ ...cfg, linking_charge: e.target.value })} className={inputCls} data-testid="admin-flex-linking-input" />
              </label>
            </div>
          </section>
          <Section title="Master software packages" list="master_options" />
          <Section title="Slave software packages" list="slave_options" />
          <button onClick={save} disabled={busy} data-testid="admin-flex-save-button" className="bg-regal-amber px-12 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-60">
            {busy ? "Saving…" : "Save FLEX Options"}
          </button>
        </div>
      </div>
    </main>
  );
}
